import {
  contact1,
  live,
  menuContact1Crop,
  menuLiveCrop,
  menuWatchCrop,
  riverLogga,
  unmute,
  unmuted,
  watch,
} from "../assets";
import { useBackgroundAudioContext } from "../BackgroundAudio";
import { Link } from "../router";

/** The home button: River wordmark in the top-left corner of every subpage.
 * It goes to `/` — it has never read history — so it is a masthead you press,
 * not a back button. Ticket 023. */
export function HomeButton() {
  return (
    <Link to="/" className="home-button" data-audio="keep">
      <img src={riverLogga} alt="River" />
    </Link>
  );
}

/**
 * Two drawings of each word, because the same word is two different crops.
 *
 * `watch.png` is 538×223 and `menu_watch_crop.png` 372×101 — the nav's assets
 * and the landing page's menu row's are not the same files. Ticket 025 makes
 * /watch's masthead pixel-identical to the landing page's on a portrait phone,
 * and pixel-identical means the crops, so each link carries both and
 * src/mobile/portrait-subpages.css swaps them for that one viewport. 026 and 027
 * gave /contact and /live the same masthead, so on a portrait phone all three
 * subpages wear the crops; every other viewport keeps the nav's own drawings,
 * held off by `hidden` — the UA stylesheet's `display: none`, which any author
 * rule outranks.
 *
 * Carrying both on all three pages rather than only on /watch is what keeps the
 * yellow free: `.site-nav a.active img` (global.css) goes on marking the current
 * page whichever drawing is showing, with no second rule to keep in step.
 */
const NAV = [
  { to: "/watch", src: watch, crop: menuWatchCrop, alt: "Watch" },
  { to: "/live", src: live, crop: menuLiveCrop, alt: "Live" },
  { to: "/contact", src: contact1, crop: menuContact1Crop, alt: "Contact" },
];

/** Watch / Live / Contact nav in the top-right corner of the subpages. */
export function SiteNav({ active }: { active: string }) {
  return (
    <nav className="site-nav" data-audio="keep">
      {NAV.map(({ to, src, crop, alt }) => (
        // `nav-watch` / `nav-live` / `nav-contact`: the mobile crops are three
        // different widths, so each one needs a name to be sized by.
        <Link key={to} to={to} className={`nav-${to.slice(1)}${to === active ? " active" : ""}`}>
          <img className="nav-art" src={src} alt={alt} />
          <img className="nav-crop" src={crop} alt={alt} hidden />
        </Link>
      ))}
    </nav>
  );
}

/**
 * Which icon the toggle wears, and whether it is glowing. Split out so both
 * branches can be checked without a renderer — the component itself is one
 * `<img>` around this.
 */
export function muteFace(muted: boolean) {
  return {
    // `interactive` is the hover glow and the pointer cursor; it is on the
    // muted state only, which is the one asking to be clicked.
    className: muted ? "mute-toggle interactive" : "mute-toggle",
    src: muted ? unmute : unmuted,
  };
}

/**
 * The switch for the loop `BackgroundAudioProvider` owns. Ticket 013 moved the
 * `<audio>` above the router so it survives navigation; this is the other half
 * — the control has to outlive the landing page too, or /watch, /live and
 * /contact play music with nothing on screen to stop it. Ticket 024.
 *
 * Rendered by all four pages and positioned by the page it is on: Home draws it
 * on its own canvas in percentages (`.page-home .mute-toggle`, Home.css), the
 * three subpages have no canvas to hang it on and pin it top centre between the
 * wordmark and the nav (global.css). A class rather than an id because /live is
 * mounted twice — once visible, once hidden and `inert` — so on every other page
 * there are two of these in the document. See src/App.tsx.
 */
export function MuteToggle() {
  const { muted, toggleMute } = useBackgroundAudioContext();
  const { className, src } = muteFace(muted);
  // `data-audio="keep"`: ticket 028's press-to-mute must not reach the control
  // that presses back — this is the one element that toggles rather than mutes.
  return (
    <img className={className} src={src} alt="Ljud av/på" onClick={toggleMute} data-audio="keep" />
  );
}
